import { t } from '../utils/i18n'
/**
 * IncidentDetailPanel — Caso unificado de un incidente
 *
 * Reutilizable desde IncidentsView (split-screen), Dashboard (toast click),
 * MapView (popup de sitio) o cualquier WS broadcast incident_created.
 *
 * Layout vertical en 4 zonas:
 *   1. Header — id, severity, status, sitio, asignado, origen
 *   2. Evidencia — snapshot principal + crops correlacionados + acciones rápidas
 *   3. Timeline — IncidentEvent + edge_events ±5min + PTT
 *   4. Acciones — estado, escalamiento S1/S2/S3, falso positivo, nota
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuthStore } from '../store/authStore'

// ─── Helpers ────────────────────────────────────────────────────────────────

const _idpEN = typeof window !== 'undefined' && window.location.hostname === 'cad.retaliq.net'
const SEV_COLOR = {
  low:      { bg: '#1f2937', text: '#9ca3af', label: _idpEN ? 'Low'      : 'Bajo'    },
  medium:   { bg: '#78350f', text: '#fcd34d', label: _idpEN ? 'Medium'   : 'Medio'   },
  high:     { bg: '#7f1d1d', text: '#fca5a5', label: _idpEN ? 'High'     : 'Alto'    },
  critical: { bg: '#581c87', text: '#d8b4fe', label: _idpEN ? 'Critical' : 'Crítico' },
}

const STATUS_LABEL = _idpEN ? {
  open: 'Open', acknowledged: 'Acknowledged',
  in_progress: 'In progress', resolved: 'Resolved', closed: 'Closed',
} : {
  open: 'Abierto', acknowledged: 'Reconocido',
  in_progress: 'En curso', resolved: 'Resuelto', closed: 'Cerrado',
}

const SOURCE_META = _idpEN ? {
  operator     : { icon: '👤', label: 'Operator' },
  guard_report : { icon: '📱', label: 'Guard report' },
  edge_alert   : { icon: '🚨', label: 'AI detection' },
  ai_proactive : { icon: '🤖', label: 'Proactive AI' },
} : {
  operator     : { icon: '👤', label: 'Operador' },
  guard_report : { icon: '📱', label: 'Reporte guardia' },
  edge_alert   : { icon: '🚨', label: 'Detección IA' },
  ai_proactive : { icon: '🤖', label: 'IA proactiva' },
}

const EVENT_ICONS = {
  weapon_detected   : '🔫',
  loitering_detected: '⏱️',
  line_crossing     : '🚧',
  face_match        : '👤',
  vehicle_detected  : '🚗',
  person_detected   : '🚶',
  lpr               : '🪪',
  guard_report      : '📱',

  // Audio (YAMNet)
  sound_gunshot     : '🔫',
  sound_explosion   : '💥',
  sound_glass       : '🔨',
  sound_scream      : '😱',
  sound_distress    : '😢',
  sound_alarm       : '🚨',
  sound_siren       : '🚓',

  created           : '➕',
  escalation        : '⬆️',
  false_positive    : '⚠️',
  note              : '📝',
  snapshot          : '📷',
  assignment        : '🎯',
  status_change     : '🔄',
}

// Etiqueta visible de las reglas del correlator. Si la regla no está acá
// se muestra el rule_id crudo (sirve para reglas nuevas que aún no tienen
// label asignado).
const CORRELATION_LABEL = _idpEN ? {
  R1_weapon_convergence         : 'Weapon+person convergence',
  R4_operator_guard_coordination: 'Guard report over AI alert',
  R5_same_type_cascade          : 'Same-type cascade',
} : {
  R1_weapon_convergence         : 'Convergencia arma+persona',
  R4_operator_guard_coordination: 'Reporte guardia sobre alerta IA',
  R5_same_type_cascade          : 'Cascada del mismo tipo',
}

function fmtTime(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleTimeString(_idpEN ? 'en-US' : 'es-CL',
      { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  } catch { return iso.slice(11, 19) }
}

function fmtDateTime(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString(_idpEN ? 'en-US' : 'es-CL',
      { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  } catch { return iso }
}

// ─── Subcomponentes ────────────────────────────────────────────────────────

function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
      textTransform: 'uppercase', letterSpacing: '0.08em',
      padding: '14px 14px 6px',
    }}>{children}</div>
  )
}

function HeaderBlock({ incident }) {
  const sev = SEV_COLOR[incident.severity] || SEV_COLOR.low
  const src = SOURCE_META[incident.source] || { icon: '◆', label: incident.source || '?' }
  return (
    <div style={{
      padding: '14px 16px',
      background: 'var(--bg-surface)',
      borderBottom: '1px solid var(--border)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
          background: sev.bg, color: sev.text, letterSpacing: '0.05em',
        }}>{sev.label.toUpperCase()}</span>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>#{incident.id}</span>
        <span style={{
          fontSize: 10, padding: '2px 6px', borderRadius: 3,
          background: 'var(--bg-muted)', color: 'var(--text-secondary)',
        }}>{STATUS_LABEL[incident.status] || incident.status}</span>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
        {incident.title}
      </div>
      {incident.description && (
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>
          {incident.description}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 11, color: 'var(--text-muted)' }}>
        <span>🕐 {fmtDateTime(incident.created_at)}</span>
        <span>{src.icon} {src.label}</span>
        {incident.lat && incident.lon && (
          <span title={`±${incident.gps_accuracy || '?'}m`}>
            📍 {incident.lat.toFixed(4)}, {incident.lon.toFixed(4)}
          </span>
        )}
      </div>
    </div>
  )
}

// event_types que disparan generación de clip MP4 6s (mantener en sync con
// edge/sensors/clip_extractor.py INCIDENT_TRIGGER_TYPES). Si el evento es
// de uno de estos tipos, el ring buffer + extractor lo grabó. Si no es,
// no perdemos tiempo intentando cargar un clip que no existe.
const CLIP_EVENT_TYPES = new Set([
  'loitering', 'loitering_detected',
  'line_crossing',
  'weapon_detected',
  'person_alert',
  'intrusion',
  'lpr',
  'incident',
])

function EvidenceBlock({ incident, edgeEvents, sensorsGrant }) {
  const cropUrl = (uuid, kind = 'crop') => {
    if (!sensorsGrant || !uuid) return null
    return `${sensorsGrant.base_url}/sensors/${uuid}/${kind}.jpg`
         + `?token=${encodeURIComponent(sensorsGrant.token)}`
  }
  const clipUrl = (uuid) => {
    if (!sensorsGrant || !uuid) return null
    return `${sensorsGrant.base_url}/sensors/${uuid}/clip.mp4`
         + `?token=${encodeURIComponent(sensorsGrant.token)}`
  }
  const snapshot = incident.snapshot_b64 ? `data:image/jpeg;base64,${incident.snapshot_b64}` : null

  // Cámara del incident: el title sigue el formato "{tipo} · {camera_name}".
  // El backend trae edgeEvents de TODAS las cámaras del sitio ±5min, así que
  // sin filtrar elegíamos el clip de la cámara equivocada. Filtramos por
  // camera_name antes de buscar el trigger. Si el formato del title cambia
  // o no calza con ninguna camera_name de los eventos, cae al comportamiento
  // viejo (cualquier evento con sensor_uuid).
  const incidentCamera = (() => {
    const t = incident?.title || ''
    const idx = t.lastIndexOf('·')
    return idx >= 0 ? t.slice(idx + 1).trim() : null
  })()

  const sameCamEvents = incidentCamera
    ? edgeEvents.filter(e => e.camera_name === incidentCamera)
    : edgeEvents
  const sameCamWithCrop = sameCamEvents.filter(e => e.sensor_uuid)
  const eventsWithCrop = edgeEvents.filter(e => e.sensor_uuid)

  // Evento "principal" del incidente — el que dispara el clip.
  // Prioridad: 1) mismo nombre de cámara que el incident + tipo excepción,
  // 2) mismo nombre de cámara con cualquier sensor_uuid,
  // 3) cualquier tipo excepción con sensor_uuid (fallback histórico),
  // 4) cualquier evento con sensor_uuid, 5) nada.
  const triggerEvent =
    sameCamEvents.find(e => e.sensor_uuid && CLIP_EVENT_TYPES.has(e.event_type))
    || sameCamWithCrop[0]
    || edgeEvents.find(e => e.sensor_uuid && CLIP_EVENT_TYPES.has(e.event_type))
    || eventsWithCrop[0]
    || null

  // Estado del clip: probing | showing | not-available
  // Hacemos un preflight Range request al endpoint /clip.mp4 ANTES de
  // mostrar el <video>. Razón: cuando el sidecar devuelve 404 con body
  // JSON ({"detail":"Clip no disponible..."}), Chrome NO dispara el
  // onError del <video> y se queda colgado en networkState=2 indefinido.
  // El preflight nos dice arriba si vale la pena renderizar el video.
  const [clipState, setClipState] = useState('probing')
  const clipHref = triggerEvent ? clipUrl(triggerEvent.sensor_uuid) : null

  useEffect(() => {
    if (!clipHref) { setClipState('not-available'); return }
    setClipState('probing')
    let cancelled = false
    fetch(clipHref, { method: 'GET', headers: { Range: 'bytes=0-0' } })
      .then(r => {
        if (cancelled) return
        if (r.status === 200 || r.status === 206) setClipState('showing')
        else setClipState('not-available')
      })
      .catch(() => { if (!cancelled) setClipState('not-available') })
    return () => { cancelled = true }
  }, [clipHref, incident.id])

  // Cement India demo — servir uno de los MP4s cement-* como clip del
  // incidente:
  //   • Si el título/descripción del incidente implica una PERSONA
  //     (caída, PPE, loitering, restricted zone, machinery guard,
  //     confined space, perimeter breach, fatigue, handrail,
  //     vehicle proximity → involucra un peatón), servimos el video
  //     nuevo cement-person.mp4 que Paulo dedicó a alertas de personas.
  //   • Si el incidente es industrial no-personal (humo, fuego, ANPR,
  //     truck queue, idle truck, spillage, bag overflow, wrong lane),
  //     seguimos con el round-robin cement-1..4.
  const _isCementDemo = typeof window !== 'undefined' && window.location.hostname === 'cad.retaliq.net'
  const _incTitleLower = String(incident?.title || '').toLowerCase()
  const _incDescLower  = String(incident?.description || '').toLowerCase()
  const _incText = `${_incTitleLower} ${_incDescLower}`

  // Keywords para el video del esmeril con chispas — hot work, grinder,
  // spark shower, welding sin permiso. Se evalúa PRIMERO porque el
  // caso "grinder" contiene "operator" que también matchearía persona.
  const _GRINDER_KEYWORDS = [
    'grinder', 'grinding', 'angle grinder',
    'hot work', 'fire watch',
    'spark', 'sparks', 'spark shower',
    'welding', 'weld',
  ]
  const _isGrinderIncident = _isCementDemo &&
    _GRINDER_KEYWORDS.some(k => _incText.includes(k))

  // Keywords que indican intervención humana visible en cámara.
  const _PERSON_KEYWORDS = [
    'person', 'people', 'worker', 'operator', 'guard',
    'ppe', 'hardhat', 'hi-vis', 'vest',
    'confined space', 'buddy',
    'fall', 'fallen',
    'loitering', 'loiter',
    'restricted zone', 'restricted', 'intrusion',
    'perimeter breach', 'unauthorized',
    'machinery guard', 'guard bypass',
    'fatigue',
    'handrail',
    'vehicle proximity',   // proximidad vehículo↔trabajador
    'proximity alert',
  ]
  const _isPersonIncident = _isCementDemo && !_isGrinderIncident &&
    _PERSON_KEYWORDS.some(k => _incText.includes(k))

  const _cementIncidentClip = _isCementDemo
    ? (_isGrinderIncident
        ? '/branding/cement-videos/cement-grinder.mp4'
        : (_isPersonIncident
            ? '/branding/cement-videos/cement-person.mp4'
            : `/branding/cement-videos/cement-${((Math.abs(Number(incident?.id) || 0)) % 4) + 1}.mp4`))
    : null
  const effClipHref = _cementIncidentClip || clipHref
  const effClipState = _cementIncidentClip ? 'showing' : clipState
  const effTriggerEvent = _cementIncidentClip
    ? { camera_name: incidentCamera || 'DEMO CAM', sensor_uuid: 'demo' }
    : triggerEvent

  return (
    <>
      <SectionTitle>{t('Evidencia','Evidence')}</SectionTitle>
      <div style={{ padding: '0 14px 12px' }}>

        {/* — Clip 6s del momento exacto del incidente — */}
        {/* El operador ve esto ANTES que el snapshot estático: 3s antes
            del trigger + 3s después, autoplay con loop. Reproducción
            instantánea (+faststart), sin negociar con NVR. */}
        {effClipHref && effClipState === 'showing' && (
          <div style={{
            position: 'relative', marginBottom: 8,
            borderRadius: 6, overflow: 'hidden',
            background: '#000',
          }}>
            <video
              key={effClipHref}
              ref={el => { if (el) { el.muted = true; el.defaultMuted = true; el.play?.().catch(()=>{}); } }}
              src={effClipHref}
              autoPlay muted loop playsInline controls
              preload="auto"
              onError={() => { if (!_isCementDemo) setClipState('not-available') }}
              style={{
                width: '100%', maxHeight: 280,
                display: 'block', objectFit: 'contain',
                background: '#000',
              }}
            />
            <div style={{
              position: 'absolute', top: 8, left: 8,
              background: 'rgba(0,0,0,0.75)', color: '#fff',
              fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
              padding: '3px 8px', borderRadius: 3,
              textTransform: 'uppercase',
            }}>● {_isCementDemo ? 'Live Feed' : 'Clip 6s'} · {effTriggerEvent.camera_name}</div>
          </div>
        )}

        {/* Fallback: clip 404 (no se generó o expiró) — mostrar snapshot */}
        {snapshot && (!clipHref || clipState === 'not-available') && (
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <img src={snapshot} alt="snapshot principal"
              style={{
                width: '100%', borderRadius: 6, maxHeight: 220,
                objectFit: 'cover', background: '#000',
              }} />
            {clipHref && clipState === 'not-available' && (
              <div style={{
                position: 'absolute', top: 8, left: 8,
                background: 'rgba(120,53,15,0.85)', color: '#fcd34d',
                fontSize: 10, fontWeight: 600,
                padding: '3px 8px', borderRadius: 3,
              }}>{_idpEN ? 'Video clip not yet available' : 'Clip de video aún no disponible'}</div>
            )}
          </div>
        )}

        {/* — Crops correlacionados — primero los de la cámara del incident,
            después el resto del sitio como contexto. Así el operador siempre
            ve la evidencia visual de la cámara que originó el caso arriba. */}
        {eventsWithCrop.length > 0 && (() => {
          const otherCamWithCrop = eventsWithCrop.filter(
            e => e.camera_name !== incidentCamera
          )
          const ordered = [...sameCamWithCrop, ...otherCamWithCrop]
          return (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4,
            marginBottom: 8,
          }}>
            {ordered.slice(0, 6).map(e => (
              <a key={e.id} href={cropUrl(e.sensor_uuid, 'frame')}
                target="_blank" rel="noopener noreferrer"
                title={`${e.camera_name} · ${fmtTime(e.ts)} · ${e.event_type}`}
                style={{ display: 'block', aspectRatio: '1/1',
                         background: '#000', borderRadius: 4, overflow: 'hidden',
                         position: 'relative' }}>
                <img src={cropUrl(e.sensor_uuid, 'crop')} alt=""
                  onError={ev => { ev.target.style.display = 'none' }}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
                  color: '#fff', fontSize: 9, padding: '6px 3px 2px',
                  textAlign: 'center', fontWeight: 600,
                }}>{fmtTime(e.ts).slice(0, 5)}</div>
              </a>
            ))}
          </div>
          )
        })()}
        {!snapshot && eventsWithCrop.length === 0 && !clipHref && (
          <div style={{
            padding: 20, textAlign: 'center', color: 'var(--text-muted)',
            fontSize: 11, background: 'var(--bg-muted)', borderRadius: 4,
          }}>{_idpEN ? 'No visual evidence available' : 'Sin evidencia visual disponible'}</div>
        )}
      </div>
    </>
  )
}

function CorrelationBadge({ rule, score }) {
  if (!rule) return null
  const label = CORRELATION_LABEL[rule] || rule
  return (
    <span title={`Regla: ${rule} · score ${(score ?? 0).toFixed(2)}`} style={{
      fontSize: 9, fontWeight: 600,
      padding: '1px 5px', borderRadius: 3,
      background: 'rgba(99,102,241,0.18)',
      color: '#a5b4fc',
      marginLeft: 6, whiteSpace: 'nowrap',
    }}>🔗 {label}</span>
  )
}

function TimelineBlock({ timeline, edgeEvents, pttRecordings }) {
  // Fusionar timeline + edge events + PTT en orden cronológico
  const items = [
    ...timeline.map(t => ({ kind: 'timeline', ts: t.created_at, ...t })),
    ...edgeEvents.map(e => ({ kind: 'edge', ts: e.ts, ...e })),
    ...pttRecordings.map(p => ({ kind: 'ptt', ts: p.started_at, ...p })),
  ].sort((a, b) => new Date(a.ts || 0) - new Date(b.ts || 0))

  if (items.length === 0) return null

  // Contar cuántos vienen del correlator (cualquier IncidentEvent del timeline
  // que tenga data.correlation_rule). Esto le dice al operador "este caso
  // creció con N adjunciones automáticas" — refuerza la sensación de PSIM real.
  const correlatedCount = items.filter(
    it => it.kind === 'timeline' && it.data?.correlation_rule
  ).length

  return (
    <>
      <SectionTitle>
        {_idpEN ? 'Case timeline' : 'Timeline del caso'} · {items.length} {_idpEN ? 'events' : 'eventos'}
        {correlatedCount > 0 && (
          <span style={{
            marginLeft: 8, fontSize: 9, fontWeight: 600,
            padding: '1px 5px', borderRadius: 3,
            background: 'rgba(99,102,241,0.18)', color: '#a5b4fc',
            textTransform: 'none', letterSpacing: 0,
          }}>🔗 {correlatedCount} {_idpEN ? 'auto-correlated' : 'auto-correlacionados'}</span>
        )}
      </SectionTitle>
      <div style={{ padding: '0 14px 12px', maxHeight: 320, overflowY: 'auto' }}>
        {items.map((it, i) => {
          let icon = EVENT_ICONS[it.event_type || it.type] || '◆'
          let title, sub
          const corrRule  = it.data?.correlation_rule
          const corrScore = it.data?.correlation_score
          if (it.kind === 'edge') {
            title = `${it.event_type.replace('_', ' ')} · ${it.camera_name || '?'}`
            const parts = []
            if (it.vehicle_type) parts.push(it.vehicle_type)
            if (it.color) parts.push(it.color)
            if (it.make) parts.push(it.make)
            if (it.plate) parts.push(it.plate)
            if (it.confidence) parts.push(`${Math.round(it.confidence * 100)}%`)
            sub = parts.join(' · ')
          } else if (it.kind === 'ptt') {
            icon = '📻'
            title = `PTT · ${it.caller || 'Operador'}`
            sub = it.duration_s ? `${Math.round(it.duration_s)}s` : 'en curso'
          } else {
            title = it.description || it.type
            sub = ''
          }
          return (
            <div key={`${it.kind}-${it.id || i}`} style={{
              display: 'flex', gap: 8, padding: '6px 0',
              borderBottom: '1px solid var(--border)',
              fontSize: 11,
            }}>
              <span style={{ fontSize: 14, flexShrink: 0 }}>{icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                  {title}
                  <CorrelationBadge rule={corrRule} score={corrScore} />
                </div>
                {sub && (
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                    {sub}
                  </div>
                )}
              </div>
              <span style={{ fontSize: 10, color: 'var(--text-muted)',
                             fontFamily: 'monospace', flexShrink: 0 }}>
                {fmtTime(it.ts)}
              </span>
            </div>
          )
        })}
      </div>
    </>
  )
}

function ActionsBlock({ incident, playbooks, token, onUpdated }) {
  const [busy, setBusy] = useState(null)
  const [note, setNote] = useState('')
  const [error, setError] = useState(null)

  const post = useCallback(async (path, body) => {
    setBusy(path); setError(null)
    try {
      const r = await fetch(`/api/incidents/${incident.id}${path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      })
      if (!r.ok) throw new Error(`${r.status} ${await r.text()}`)
      const d = await r.json()
      onUpdated?.(d.incident || d)
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(null)
    }
  }, [incident.id, token, onUpdated])

  const escalateTo = (level) => post('/escalate', { level, note: note || null })
  const markFalsePositive = () => post('/false_positive', { reason: note || null })

  const isClosed = ['resolved', 'closed'].includes(incident.status)

  return (
    <>
      <SectionTitle>{t('Estado y proceso','Status & process')}</SectionTitle>
      <div style={{ padding: '0 14px 12px' }}>
        {error && (
          <div style={{ padding: 6, fontSize: 11, color: '#dc2626',
                        background: 'rgba(239,68,68,0.1)', borderRadius: 4,
                        marginBottom: 8 }}>{error}</div>
        )}

        {!isClosed && (
          <button onClick={markFalsePositive} disabled={busy}
            style={{
              width: '100%', padding: '10px', marginBottom: 6,
              background: 'var(--bg-muted)', color: 'var(--text-primary)',
              border: '1px solid var(--border)', borderRadius: 5,
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>
            {busy === '/false_positive' ? (_idpEN ? 'Marking…' : 'Marcando…') : (_idpEN ? '⚠️ Mark as false positive' : '⚠️ Marcar como falso positivo')}
          </button>
        )}

        {!isClosed && (
          <>
            <SectionTitle>{_idpEN ? 'Escalation — Response playbooks' : 'Escalamiento — Playbooks Copec'}</SectionTitle>
            {Object.entries(playbooks).map(([level, pb]) => (
              <div key={level} style={{
                padding: 8, marginBottom: 6, borderRadius: 5,
                border: `1px solid ${pb.color}66`,
                background: `${pb.color}11`,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  marginBottom: 4,
                }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 6px',
                    borderRadius: 3, background: pb.color, color: '#fff',
                  }}>{level}</span>
                  <span style={{ fontSize: 11, fontWeight: 600 }}>{pb.name}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 9, color: 'var(--text-muted)' }}>
                    ≤ {pb.max_response_seconds}s
                  </span>
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 6 }}>
                  {pb.description}
                </div>
                <button onClick={() => escalateTo(level)} disabled={busy}
                  style={{
                    width: '100%', padding: '6px 10px',
                    background: pb.color, color: '#fff', border: 'none',
                    borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    opacity: busy ? 0.6 : 1,
                  }}>
                  {busy === '/escalate' ? (_idpEN ? 'Escalating…' : 'Escalando…') : (_idpEN ? `Escalate to ${level}` : `Escalar a ${level}`)}
                </button>
              </div>
            ))}
          </>
        )}

        <SectionTitle>{_idpEN ? 'Note / reason' : 'Nota / motivo'}</SectionTitle>
        <textarea value={note} onChange={e => setNote(e.target.value)}
          placeholder={_idpEN ? "Optional — appears in the timeline and action log" : "Opcional — aparece en la timeline y en la acción"}
          rows={2}
          style={{
            width: '100%', padding: '6px 8px', fontSize: 11,
            background: 'var(--bg-root)', color: 'var(--text-primary)',
            border: '1px solid var(--border)', borderRadius: 4,
            boxSizing: 'border-box', resize: 'vertical',
            fontFamily: 'inherit',
          }} />
      </div>
    </>
  )
}

// ─── IncidentDetailPanel ───────────────────────────────────────────────────

export default function IncidentDetailPanel({ incidentId, onClose, onUpdated }) {
  const token = useAuthStore(s => s.token)
  const [ctx, setCtx] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const loadContext = useCallback(async () => {
    if (!incidentId || !token) return
    setLoading(true); setError(null)
    try {
      const r = await fetch(`/api/incidents/${incidentId}/context`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!r.ok) throw new Error(`${r.status}`)
      setCtx(await r.json())
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [incidentId, token])

  useEffect(() => { loadContext() }, [loadContext])

  const handleUpdated = useCallback((updated) => {
    // Recargar el context completo cuando una acción modifica el incident
    loadContext()
    onUpdated?.(updated)
  }, [loadContext, onUpdated])

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: 'var(--bg-root)',
      borderLeft: '1px solid var(--border)',
      overflow: 'hidden',
    }}>
      {onClose && (
        <button onClick={onClose}
          style={{
            position: 'absolute', top: 10, right: 10, zIndex: 5,
            background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none',
            borderRadius: '50%', width: 28, height: 28, cursor: 'pointer',
            fontSize: 14,
          }}>✕</button>
      )}

      {loading && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', color: 'var(--text-muted)',
                      fontSize: 12 }}>Cargando caso…</div>
      )}
      {error && (
        <div style={{ padding: 14, color: '#dc2626', fontSize: 12 }}>
          Error: {error}
        </div>
      )}

      {ctx && (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <HeaderBlock incident={ctx.incident} />
          <EvidenceBlock incident={ctx.incident}
            edgeEvents={ctx.edge_events}
            sensorsGrant={ctx.sensors_grant} />
          <TimelineBlock timeline={ctx.timeline}
            edgeEvents={ctx.edge_events}
            pttRecordings={ctx.ptt_recordings} />
          <ActionsBlock incident={ctx.incident}
            playbooks={ctx.playbooks}
            token={token}
            onUpdated={handleUpdated} />
        </div>
      )}
    </div>
  )
}
